import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { HERO_SLIDES } from "@/data/site";

export default function HeroSlider() {
  const [idx, setIdx] = useState(0);
  const slide = HERO_SLIDES[idx];

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative h-[78vh] min-h-[520px] overflow-hidden bg-brand-navy" data-testid="hero-slider">
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.06 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] }}
          className="absolute inset-0"
        >
          <img src={slide.image} alt={slide.big} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-brand-navy/30 to-brand-navy/40" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-10 flex flex-col items-center justify-center text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <p className="font-heading italic text-2xl sm:text-4xl text-white/90">{slide.pre}</p>
            <h1 className="mt-1 font-heading font-bold uppercase tracking-wide text-yellow-400 text-6xl sm:text-8xl lg:text-9xl leading-none drop-shadow-[0_6px_20px_rgba(0,0,0,0.45)]">
              {slide.big}
            </h1>
            <p className="mt-1 font-heading italic text-2xl sm:text-4xl text-white/90">{slide.post}</p>
            <p className="mt-5 text-white/85 font-body text-base sm:text-lg max-w-xl mx-auto">{slide.sub}</p>
            <Link
              to={`/packages?q=${encodeURIComponent(slide.q)}`}
              data-testid="hero-book-now"
              className="mt-8 inline-flex items-center rounded-full bg-brand-orange px-10 py-4 text-base font-bold uppercase tracking-wider text-white shadow-[0_0_28px_rgba(240,128,32,0.55)] transition-transform hover:-translate-y-0.5"
            >
              Book Now
            </Link>
          </motion.div>
        </AnimatePresence>
      </div>

      <button
        aria-label="Previous slide"
        data-testid="hero-prev"
        onClick={() => setIdx((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 backdrop-blur text-white transition-colors hover:bg-brand-orange"
      >
        <CaretLeft size={22} weight="bold" />
      </button>
      <button
        aria-label="Next slide"
        data-testid="hero-next"
        onClick={() => setIdx((i) => (i + 1) % HERO_SLIDES.length)}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 backdrop-blur text-white transition-colors hover:bg-brand-orange"
      >
        <CaretRight size={22} weight="bold" />
      </button>

      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            aria-label={`Slide ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-2.5 rounded-full transition-all ${i === idx ? "w-8 bg-yellow-400" : "w-2.5 bg-white/50"}`}
          />
        ))}
      </div>

      {/* torn paper edge */}
      <svg className="absolute bottom-0 left-0 right-0 z-20 w-full h-14 text-brand-cream" viewBox="0 0 1440 60" preserveAspectRatio="none" fill="currentColor">
        <path d="M0,60 L0,38 L48,44 L96,30 L144,42 L192,28 L240,40 L288,32 L336,46 L384,30 L432,42 L480,26 L528,40 L576,34 L624,46 L672,28 L720,42 L768,30 L816,44 L864,32 L912,44 L960,28 L1008,42 L1056,30 L1104,44 L1152,34 L1200,46 L1248,28 L1296,42 L1344,32 L1392,44 L1440,30 L1440,60 Z" />
      </svg>
    </section>
  );
}
